import { Task } from "@freeconvert/freeconvert-node/dist/types";
import { useDeviceImport } from "client/hooks/import/useDeviceImport";
import { useExternalImport } from "client/hooks/import/useExternalImport";
import { useDeviceUpload } from "client/hooks/upload/useDeviceUpload";
import { useToggle } from "client/hooks/utils/useToggle";
import { FileData } from "client/types/FileData";
import { HandleFile, UploadSettings } from "client/types/HandleFile";
import { formatBytes } from "client/utils/formatBytes";
import { getImageExtension } from "client/utils/getImageExtension";
import { getRandomId } from "client/utils/getRandomId";
import { useCreateImportTask } from "client/utils/useCreateImportTask";
import { DEFAULT_TOAST_SHOW_TIME } from "components/common/Toast/ToastRenderer";
import { useToast } from "components/common/Toast/useToast";
import { MAX_UPLOAD_LIMIT } from "components/modules/Uploader/uploaderUtils";
import {
    addImage,
    deleteImage,
    importComplete,
    setError,
    setImportTask,
    setProgress,
    setState,
} from "store/features/compress/actions";
import { CompressImage, CompressState } from "store/features/compress/types";
import { INITIAL_COMPRESS_QUALITY } from "store/features/compress/utils";
import { createImportTask } from "store/features/imageUploader/extraReducers/createImportTask";
import { FileState, UploadType } from "store/features/imageUploader/types";
import { store, useAppDispatch } from "store/store";
import { Size } from "store/types/previewImage";
import { useCompress } from "../../useCompress";
import { Socket } from "socket.io-client";
import { getSocket } from "client/utils/getSocket";
import { getFCUrl } from "client/utils/getFCUrl";
import { translateFunc } from "client/utils/translateFunc";
import { getCompressionSettings } from "store/features/compress/utils/getCompressionSettings";
import { useBatchImport } from "client/hooks/import/useBatchImport";
import { extensionsConfig } from "client/config/extensionsConfig";
import { useRouter } from "next/router";
import { useDailyQuota } from "@/modules/DailyQuota/hooks/useDailyQuota";
import { useValidateMaxFileSize } from "@/modules/DailyQuota/hooks/useValidateMaxFileSize";

let socket: Socket | null = null;

export const useAddImages = (onSubmit?: () => void) => {
    const { pathname } = useRouter();
    const supportedExtensions =
        (extensionsConfig as any)[pathname?.split("/")[1] || ""] || extensionsConfig["image-compressor"];
    const { isOpen, toggle, onClose } = useToggle();
    const dispatch = useAppDispatch();
    const { toast, removeBatch } = useToast();
    const { compressImage } = useCompress();
    const { validateMaxFileSize } = useValidateMaxFileSize();
    const {
        showMaxFileSizeErrorToast,
        getBatchLimit,
        showBatchLimitErrorToast,
        getRemainingQuota,
        showQuotaLimitExceededDeletedImagesToast,
        getRemainingQuotaSynchronously,
        getBatchLimitSynchronously,
    } = useDailyQuota();

    const onStart = (task: Task, progress?: FileState["progress"]) => {};

    const onComplete = async (task: Task, metaData: { image: CompressImage; uploadType: UploadType }) => {
        const size = task.result?.size;
        if (!size || !(await validateMaxFileSize([size]))) {
            showMaxFileSizeErrorToast();
            dispatch(deleteImage(metaData.image.id));
            return;
        }
        dispatch(
            importComplete({
                imageId: metaData.image.id,
                src: getFCUrl(
                    metaData.uploadType === "device" ? metaData.image.src : (task.result?.url?.toString() ?? ""),
                ),
                importTask: task,
            }),
        );
        compressImage(
            metaData.image,
            task,
            getCompressionSettings(
                getImageExtension(metaData.image.file.file),
                store.getState().compress.compressionSettings,
            ),
            metaData.image.file.file.name,
        );
        proceedNextImports();
    };

    const onFailure = (_task: Task, metaData: { image: CompressImage }) => {
        dispatch(
            setError({
                imageId: metaData.image.id,
                error: {
                    state: "importing",
                },
            }),
        );
        proceedNextImports();
    };

    const onOfflineLimitReach = (task: Task) => {};

    const onProgress = (task: Task, progress: number, _speed: Size, metaData: { image: CompressImage }) => {
        dispatch(
            setProgress({
                imageId: metaData.image.id,
                progress,
            }),
        );
    };
    const onDisconnectText = `${translateFunc("you-are-offline")} ${translateFunc("trying-to-reconnect")}`;
    const onDisconnect = () => {
        toast({
            closeable: true,
            position: "top-right",
            status: "error",
            batch: "disconnect",
            text: onDisconnectText,
        });
    };

    const onReconnectText = translateFunc("online-again-toast");
    const onReconnect = () => {
        toast({
            closeable: true,
            position: "top-right",
            status: "success",
            batch: "reconnect",
            text: onReconnectText,
            time: DEFAULT_TOAST_SHOW_TIME,
        });
        removeBatch("disconnect");
    };

    const { handleDeviceImport } = useDeviceImport({
        onStart,
        onComplete,
        onFailure,
        onOfflineLimitReach,
        onProgress,
        onDisconnect,
        onReconnect,
    });

    const { handleExternalImport } = useExternalImport({
        onStart,
        onComplete,
        onFailure,
        onOfflineLimitReach,
        onDisconnect,
        onReconnect,
    });

    const onImportStart = (image: CompressImage) => {
        dispatch(
            setState({
                imageId: image.id,
                imageState: "importing",
            }),
        );
        handleCreateImportTask<CompressImage>(
            image,
            image.file,
            image.uploadType,
            image.settings,
            createImportTask,
            handleCreateImportTaskSuccess,
        );
    };

    const getImportingImages = (): CompressImage[] =>
        store.getState().compress.images.filter((image) => image.state === "importing");

    const getAddedImages = (): CompressImage[] =>
        store.getState().compress.images.filter((image) => image.state === "added");

    const { handleBatchedSingleFile, proceedNextImports } = useBatchImport(
        onImportStart,
        getImportingImages,
        getAddedImages,
        false,
    );

    const handleFile: HandleFile = async (files, uploadType, settings) => {
        if (!files[0]) {
            // No files selected
            return;
        }

        const remainingQuota = await getRemainingQuota();
        const batchLimit = await getBatchLimit();
        let batchSize = files.length + store.getState().compress.images.length;

        if (files.length > remainingQuota) {
            showQuotaLimitExceededDeletedImagesToast();
            while (files.length + store.getState().imageUploader.images.length > remainingQuota) {
                if (files.length) {
                    files.pop();
                } else if (store.getState().imageUploader.images.length) {
                    const img = store.getState().imageUploader.images[-1];
                    dispatch(deleteImage(img.id));
                }
            }
        }

        batchSize = files.length + store.getState().compress.images.length;
        if (batchSize > batchLimit) {
            showBatchLimitErrorToast();

            while (files.length + store.getState().compress.images.length > batchLimit) {
                if (files.length) {
                    files.pop();
                } else if (store.getState().compress.images.length) {
                    const img = store.getState().compress.images[-1];
                    dispatch(deleteImage(img.id));
                }
            }
        }

        if (uploadType === "device" && !(await validateMaxFileSize(files.map((file) => file.file.size)))) {
            showMaxFileSizeErrorToast();
            return;
        }

        socket = socket ?? (await getSocket());

        onClose();
        onSubmit && onSubmit();

        files.forEach(async (file, index) => {
            const image = handleNewImage(file, uploadType, settings);
            if (!image) return;
            return handleBatchedSingleFile(image, index);
        });
    };

    const { handleCreateImportTask } = useCreateImportTask();

    const handleNewImage = <T extends UploadType>(fileData: FileData, uploadType: T, settings: UploadSettings[T]) => {
        const batchLimit = getBatchLimitSynchronously();
        const remainingQuota = getRemainingQuotaSynchronously();
        const batchSize = store.getState().compress.images.length + 1;
        if (remainingQuota <= 0) {
            showQuotaLimitExceededDeletedImagesToast();
            return;
        }
        if (batchSize > batchLimit) {
            showBatchLimitErrorToast();
            return;
        }

        const { file, src } = fileData;
        const ext = getImageExtension(file);
        const compressionSettings = store.getState().compress.compressionSettings;

        const image: CompressImage = {
            id: getRandomId(),
            src,
            createdAt: new Date(),
            original: {
                width: 300,
                height: 300,
                size: formatBytes(file.size),
            },
            preview: {
                width: 300,
                height: 300,
                size: formatBytes(file.size),
            },
            quality: INITIAL_COMPRESS_QUALITY,
            title: file.name ?? "",
            format: getImageExtension(file),
            sliderValue: 50,
            importTask: null,
            state: "added",
            hasExpired: false,
            file: fileData,
            uploadType,
            settings,
            importing: {
                progress: "continuous",
            },
            compressionSettings: {
                type: compressionSettings.type,
                maxFileSize:
                    ext !== "jpg" && ext !== "jpeg"
                        ? {
                              value: 0,
                          }
                        : compressionSettings.maxFileSize,
                quality:
                    ext !== "jpg" && ext !== "jpeg"
                        ? compressionSettings.quality
                        : {
                              value: INITIAL_COMPRESS_QUALITY,
                          },
            },
        };

        dispatch(addImage(image));
        return image;
    };

    const { handleClick: handleDeviceUploadClick } = useDeviceUpload(
        handleFile,
        { allowMultiple: true, supportedExtensions },
        "compressor-editor-input",
    );

    const handleCreateImportTaskSuccess = async (
        image: CompressImage,
        task: Task,
        file: FileData,
        uploadType: UploadType,
    ) => {
        dispatch(
            setImportTask({
                imageId: image.id,
                importTask: task,
            }),
        );

        uploadType === "device"
            ? handleDeviceImport(file, task, { image: { ...image, importTask: task }, uploadType })
            : handleExternalImport(task, { image: { ...image, importTask: task }, uploadType });
    };

    return { isOpen, toggle, onClose, handleFile, handleDeviceUploadClick };
};

const equalityFn = (left: CompressState, right: CompressState) => {
    return left.images.length === right.images.length;
};
